import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Grid,
    CircularProgress,
    MenuItem,
    Chip,
    TablePagination,
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from "@mui/icons-material/Search";
import { honorariosAdminCertificadosJWT, getCombosJWT } from "../../api/endpoints";
import moment from "moment";

const estados = [
    { value: "", label: "Todos" },
    { value: "0", label: "Pendiente" },
    { value: "1", label: "Pagado" },
    { value: "2", label: "En Curso" },
    { value: "3", label: "Borrador" },
    { value: "4", label: "Cerrado" },
    { value: "-1", label: "Anulado" },
];

export default function CertificadosAdminTable() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
        fecha_hasta: moment().format("YYYY-MM-DD"),
        estado: "",
        buscar: "",
        profesional: "",
    });
    // Paginado de servidor: `data` es solo la página visible y `total` el total real.
    // Acá el volumen es alto (2.547 certificados en un mes), por eso se pagina de
    // verdad en vez de topear en 500 como estaba antes.
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [abogadoOptions, setAbogadoOptions] = useState([]);
    const [abogadoLoading, setAbogadoLoading] = useState(false);
    const [abogadoInputValue, setAbogadoInputValue] = useState("");

    const fetchAbogados = useCallback(async (searchText) => {
        const normalizeText = (text) => {
            if (!text) return "";
            return text
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };

        setAbogadoLoading(true);
        try {
            const response = await getCombosJWT('personal', searchText);
            if (response.ok) {
                // Normalize lawyer names
                const normalizedItems = (response.items || []).map(item => ({
                    ...item,
                    nombre: normalizeText(item.nombre)
                }));
                setAbogadoOptions(normalizedItems);
            }
        } catch (error) {
            console.error("Error fetching abogados", error);
            setAbogadoOptions([]);
        } finally {
            setAbogadoLoading(false);
        }
    }, []);

    // Debounce effect for abogado search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (abogadoInputValue && abogadoInputValue.length >= 2) {
                fetchAbogados(abogadoInputValue);
            } else {
                setAbogadoOptions([]);
            }
        }, 400);

        return () => clearTimeout(delayDebounceFn);
    }, [abogadoInputValue, fetchAbogados]);

    const fetchData = async (opciones = {}) => {
        const paginaPedida = opciones.pagina ?? 0;
        const porPagina = opciones.porPagina ?? rowsPerPage;
        setLoading(true);
        try {
            const fechaDesde = filters.fecha_desde;
            const fechaHasta = filters.fecha_hasta;
            const estado = filters.estado !== "" ? filters.estado : undefined;
            const buscar = filters.buscar || undefined;
            const idAbogado = filters.profesional || undefined;

            const response = await honorariosAdminCertificadosJWT(
                fechaDesde, fechaHasta, estado, buscar, idAbogado,
                porPagina, paginaPedida * porPagina
            );
            if (response.ok) {
                setData(response.data || []);
                setPage(paginaPedida);
                setTotal(typeof response.total === 'number' ? response.total : (response.data || []).length);
            }
        } catch (error) {
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            console.error("Error fetching certificados admin", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        fetchData();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        } else if (e.key === "Escape") {
            setFilters({
                fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
                fecha_hasta: moment().format("YYYY-MM-DD"),
                estado: "",
                buscar: "",
                profesional: "",
            });
            setAbogadoInputValue("");
            setAbogadoOptions([]);
        }
    };

    // Mismo criterio que CertificadosTable (pantalla del abogado): lo que cobra el
    // abogado es Costas + Costas_IVA, ya divididos por Abogados_Participantes en la
    // vista Honorarios_Consulta.
    const aCobrar = (row) => Number(row?.Costas || 0) + Number(row?.Costas_IVA || 0);
    const fmtMoneda = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

    const getEstadoColor = (idEstado) => {
        switch (Number(idEstado)) {
            case 1: return "primary"; // Pagado
            case 0: return "default"; // Pendiente
            case 2: return "secondary"; // En curso
            case 3: return "default"; // Borrador
            case 4: return "default"; // Cerrado
            case -1: return "secondary"; // Anulado
            default: return "default";
        }
    };

    const getEstadoLabel = (idEstado) => {
        switch (Number(idEstado)) {
            case 1: return "Pagado";
            case 0: return "Pendiente";
            case 2: return "En Curso";
            case 3: return "Borrador";
            case 4: return "Cerrado";
            case -1: return "Anulado";
            default: return "Desconocido";
        }
    };

    return (
        <div>
            <Paper sx={{ mb: 2, p: 2 }}>
                <form onSubmit={handleSearch}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                label="Desde"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.fecha_desde}
                                onChange={handleFilterChange("fecha_desde")}
                                onKeyDown={handleKeyDown}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                label="Hasta"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.fecha_hasta}
                                onChange={handleFilterChange("fecha_hasta")}
                                onKeyDown={handleKeyDown}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                select
                                label="Estado"
                                fullWidth
                                value={filters.estado}
                                onChange={handleFilterChange("estado")}
                            >
                                {estados.map((op) => (
                                    <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Autocomplete
                                options={abogadoOptions}
                                getOptionLabel={(option) => option.nombre || ""}
                                loading={abogadoLoading}
                                inputValue={abogadoInputValue}
                                onInputChange={(_, newInputValue) => {
                                    setAbogadoInputValue(newInputValue);
                                }}
                                onChange={(_, newValue) => {
                                    if (newValue && newValue.nombre) {
                                        setAbogadoInputValue(newValue.nombre);
                                        setFilters({ ...filters, profesional: newValue.nombre });
                                    } else if (typeof newValue === "string") {
                                        // freeSolo: cuando se presiona Enter con texto libre
                                        setFilters({ ...filters, profesional: newValue });
                                    }
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Abogado"
                                        placeholder="Buscar por nombre"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                // Actualizar filtro con el texto actual al presionar Enter
                                                setFilters({ ...filters, profesional: abogadoInputValue });
                                                handleSearch();
                                            } else if (e.key === "Escape") {
                                                setFilters({
                                                    fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
                                                    fecha_hasta: moment().format("YYYY-MM-DD"),
                                                    estado: "",
                                                    buscar: "",
                                                    profesional: "",
                                                });
                                                setAbogadoInputValue("");
                                                setAbogadoOptions([]);
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {abogadoLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                            <TextField
                                label="Búsqueda libre"
                                fullWidth
                                placeholder="Autos, Demandado, Certif (ej: A-201119057)"
                                value={filters.buscar}
                                onChange={handleFilterChange("buscar")}
                                onKeyDown={handleKeyDown}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 2 }} style={{ marginTop: 8 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                fullWidth
                                disabled={loading}
                            >
                                Buscar
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                    <CircularProgress />
                </div>
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Fecha</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Autos</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Certif</TableCell>
                                <TableCell align="right">A Cobrar</TableCell>
                                <TableCell>Demandado</TableCell>
                                <TableCell>Abogado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No se encontraron certificados.</TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {data.map((row, idx) => (
                                        <TableRow key={row.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                            <TableCell>{row.FPago}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getEstadoLabel(row.Id_Estado)}
                                                    color={getEstadoColor(row.Id_Estado)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{row.Expediente_Autos}</TableCell>
                                            <TableCell>{row.Ref_Nro_Plan}</TableCell>
                                            <TableCell>{row.Letra}-{row.Numero}</TableCell>
                                            {/* Antes mostraba row.Honorarios, que en Honorarios_Consulta es
                                                ISNULL(Partes.Honorarios, -1): una BANDERA int, no un monto. Por eso
                                                se veían "$ -1,00" y "$ 0,00". Lo que cobra el abogado es
                                                Costas + Costas_IVA, igual que en la pantalla del abogado. */}
                                            <TableCell align="right">
                                                <div>{fmtMoneda(aCobrar(row))}</div>
                                                {Number(row.Costas_IVA || 0) > 0 && (
                                                    <div style={{ fontSize: '0.7rem', color: '#6d6d6d', whiteSpace: 'nowrap' }}>
                                                        {fmtMoneda(row.Costas)} + IVA {fmtMoneda(row.Costas_IVA)}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>{row.Demandado_NN}</TableCell>
                                            <TableCell>
                                                <div>{row.Profesional || "N/A"}</div>
                                                {row.Matricula && <small style={{ color: "#666" }}>Mat: {row.Matricula}</small>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            )}
                        </TableBody>
                    </Table>
                    {/* count = total real del servidor. Cambiar de página dispara una
                        consulta nueva: los datos no están todos en memoria. */}
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(e, nuevaPagina) => fetchData({ pagina: nuevaPagina })}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            const nuevoTam = parseInt(e.target.value, 10);
                            setRowsPerPage(nuevoTam);
                            fetchData({ pagina: 0, porPagina: nuevoTam });
                        }}
                        labelRowsPerPage="Filas por página"
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from.toLocaleString('es-AR')}–${to.toLocaleString('es-AR')} de ${count.toLocaleString('es-AR')}`}
                    />
                </TableContainer>
            )}
        </div>
    );
}
